import "./App.css";
import { useEffect } from "react";
import Layout from "@components/layout/Layout"; // Re-import Layout
import Home from "@pages/Home";
import Integrations from "@pages/Integrations/Index";
import Listing from "@pages/Integrations/Listing";
import Overview from "@pages/Integrations/Overview";
import Source from "@pages/Integrations/Source";
import Destination from "@pages/Integrations/Destination";
import DataMapping from "@pages/Integrations/DataMapping";
import Review from "@pages/Integrations/Review";
import Summary from "@pages/Integrations/Summary";
import Details from "@pages/Integrations/Details";
import Workflow from "@pages/Integrations/Workflow";
import Invocations from "@pages/Integrations/Invocations";
import PipelineView from "@pages/Integrations/PipelineView";
import SequentialPipelineView from "@pages/Integrations/SequentialPipelineView";
import Login from "@pages/Login";
import EmailVerification from "@pages/EmailVerification";
import { authService } from "@services/httpClient";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "@redux/slices/authSlice";
import useSessionTimeout from "@hooks/useSessionTimeout";

import MembersList from "@pages/MembersList";
import CompaniesList from "@pages/CompaniesList";
import ConnectionsList from "@pages/ConnectionsList";
import Settings from "@pages/Settings";
import Deactivated from "@pages/Deactivated";
import AgentChat from "@pages/AgentChat";
import LaunchBuilder from "@pages/LaunchBuilder";
import ForcePasswordReset from "@pages/ForcePasswordReset";

import { Provider } from "react-redux";
import { store } from "@redux/store";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// --- Authentication Logic ---
const isAuthenticated = () => {
  return authService.getAccessToken() !== null;
};

// Helper to safely get user info
const getUserInfo = () => {
  try {
    const userInfoString = localStorage.getItem('userInfo');
    if (!userInfoString) return null;

    return JSON.parse(userInfoString);
  } catch (error) {
    console.error("Error parsing user info:", error);
    return null;
  }
};

// --- Protected Route Component ---
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const userInfo = getUserInfo();
  if (userInfo && userInfo.company_status === false) {
    return <Navigate to="/deactivated" replace />;
  }

  if (userInfo && userInfo.force_password_reset && window.location.pathname !== '/force-password-reset') {
    return <Navigate to="/force-password-reset" replace />;
  }

  return children;
};

// --- Role Protected Route Component with Redux Access ---
const RoleProtectedRouteWithRedux = ({ children, allowedRoles = [] }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const userInfo = getUserInfo();
  if (!userInfo) {
    return <Navigate to="/login" replace />;
  }

  if (userInfo.company_status === false) {
    return <Navigate to="/deactivated" replace />;
  }

  if (userInfo.force_password_reset && window.location.pathname !== '/force-password-reset') {
    return <Navigate to="/force-password-reset" replace />;
  }

  // Check if userInfo has the required fields
  if (!userInfo.role || !userInfo.email) {
    localStorage.removeItem('userInfo');
    return <Navigate to="/login" replace />;
  }

  const userRole = userInfo?.role?.toLowerCase();



  // If no specific roles are specified, allow access to all authenticated users
  if (allowedRoles.length === 0) {
    return children;
  }

  // Check if user's role is in the allowed roles
  if (userRole && allowedRoles.includes(userRole)) {
    return children;
  }

  // Access denied - redirect to login to avoid infinite loops
  toast.error("Access denied. You don't have permission to view this page.");
  return <Navigate to="/login" replace />;
};

// --- Teams OAuth popup handler — runs on every page load ---
// Popup lands here after Microsoft redirects back with ?teams_status=...
// window.opener is unreliable after cross-origin OAuth, so we write to
// localStorage and close — the parent window picks it up via storage event.
const TeamsOAuthHandler = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const teamsStatus = params.get("teams_status");
    if (!teamsStatus) return;
    // Signal the parent window via localStorage (works across cross-origin hops)
    localStorage.setItem(
      "teams_oauth_result",
      JSON.stringify({ status: teamsStatus, message: params.get("message"), ts: Date.now() })
    );
    window.close();
  }, []);
  return null;
};

// --- Session Manager (sits inside Router so useNavigate works) ---
const SessionManager = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authenticated = isAuthenticated();

  const handleExpire = useCallback(() => {
    authService.clearTokens();
    localStorage.removeItem('userInfo');
    dispatch(logout());
    toast.info('You have been signed out due to inactivity.', { toastId: 'session-timeout' });
    navigate('/login', { replace: true });
  }, [navigate, dispatch]);

  const handleWarn = useCallback(() => {
    toast.warning('You will be signed out in 1 minute due to inactivity.', {
      toastId: 'session-warning',
      autoClose: 55000,
    });
  }, []);

  useSessionTimeout({
    isAuthenticated: authenticated,
    onWarn: handleWarn,
    onExpire: handleExpire,
  });

  return null;
};

function App() {
  return (
    <Provider store={store}>
      <Router>
        <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
        <SessionManager />
        <TeamsOAuthHandler />
        <Routes>
          {/* Public Route - No Layout */}
          <Route path="/login" element={<Layout><Login /></Layout>} />
          {/* <Route path="/signup" element={<Layout><Signup /></Layout>} /> */}
          <Route path="/verify-email/:token" element={<EmailVerification />} />
          <Route path="/deactivated" element={<Deactivated />} />
          <Route path="/force-password-reset" element={<ProtectedRoute><Layout><ForcePasswordReset /></Layout></ProtectedRoute>} />

          {/* Protected Routes - With Layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Home />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute>
                <Layout>
                  {" "}
                  {/* Apply Layout here */}
                  <Integrations />
                </Layout>{" "}
                {/* Close Layout here */}
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Listing />
              }
            />
            <Route
              path="listing"
              element={
                <Listing />
              }
            />
            <Route
              path=":id/overview"
              element={
                <Overview />
              }
            />
            <Route
              path=":id/workflow"
              element={
                <Workflow />
              }
            />
            <Route
              path=":id/source"
              element={
                <Source />
              }
            />
            <Route
              path=":id/destination"
              element={
                <Destination />
              }
            />
            <Route
              path=":id/data-mapping"
              element={
                <DataMapping />
              }
            />
            <Route
              path=":id/review"
              element={
                <Review />
              }
            />
            <Route
              path=":id/summary"
              element={
                <Summary />
              }
            />
            <Route
              path=":id/details"
              element={
                <Details />
              }
            />
            <Route
              path=":id/invocations"
              element={
                <Invocations />
              }
            />
            <Route
              path="pipeline/:id"
              element={
                <PipelineView />
              }
            />
            <Route
              path="sequential/:id"
              element={
                <SequentialPipelineView />
              }
            />
          </Route>
          <Route
            path="/agent"
            element={
              <ProtectedRoute>
                <Layout>
                  <AgentChat />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Catch-all route */}
          <Route
            path="/launch-builder"
            element={
              <ProtectedRoute>
                <Layout>
                  <LaunchBuilder />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
          <Route
            path="/memberslisting"
            element={
              <RoleProtectedRouteWithRedux allowedRoles={["superadmin", "masteradmin", "admin"]}>
                <Layout>
                  <MembersList />
                </Layout>
              </RoleProtectedRouteWithRedux>
            }
          />
          <Route
            path="/companies" element={<RoleProtectedRouteWithRedux allowedRoles={["superadmin"]}><Layout><CompaniesList /></Layout></RoleProtectedRouteWithRedux>} /><Route path="/connections"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ConnectionsList />
                  </Layout>
                </ProtectedRoute>
              }
          />



          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;
